import { ArrowLeft, ArrowUpRight, Bookmark, BookMarked, Heart } from 'lucide-react'
import { useNavigate } from 'react-router'
import ROUTES from '@/ROUTES'

import { IPublicResource } from '@/MODELS/publicResource.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'
import { useResource } from '@/services/resource.service'

import Button from '@/components/Button'
import Loading from '@/components/Loading'
import { SectionHeader } from '@/components/SectionHeader'

export default function SavedResourcesPage() {
    const { user, fetchUser } = useAuth()
    const navigate = useNavigate()
    const [resources, setResources, ResourceResource] = useResource<IPublicResource[]>(ROUTES.app.publicResource)

    if (!user) {
        return <Loading isLoading={true} mode='block' text='Loading saved resources...' />
    }

    function handleToggleFavorite(resourceId: string): void {
        if (!user) return

        const resourceToToggle = resources?.find((res) => res._id === resourceId)

        if (!resourceToToggle) return

        const isAlreadyFavorited = user.favoriteResources.some(
            (favRes) => favRes?._id?.toString() === resourceId?.toString()
        )

        const updatedFavorites = isAlreadyFavorited
            ? user.favoriteResources.filter((favRes) => favRes._id !== resourceId)
            : [...user.favoriteResources, resourceToToggle]

        api(ROUTES.app.user, { _id: user._id, favoriteResources: updatedFavorites }).then(() => {
            fetchUser()
        })
    }

    return (
        <div className='flex-1 w-full mx-auto max-w-7xl'>
            <SectionHeader title='Your Saved Resources' subtitle="Resources you've bookmarked for quick access" />

            <main className='p-6 sm:mx-4'>
                {user.favoriteResources && user.favoriteResources.length > 0 ? (
                    <>
                        <div className='mb-6'>
                            <p className='text-sm text-gray-600'>
                                {user.favoriteResources.length} saved resource
                                {user.favoriteResources.length === 1 ? '' : 's'}
                            </p>
                        </div>

                        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
                            {user.favoriteResources.map((resource) => {
                                const isAlreadyFavorited = user.favoriteResources.some(
                                    (favRes) => favRes?._id?.toString() === resource._id?.toString()
                                )
                                return (
                                    <div
                                        key={resource._id}
                                        className='overflow-hidden transition-all bg-white rounded-lg shadow-sm cursor-pointer hover:shadow-md'
                                    >
                                        <div onClick={() => navigate(`/resource/${resource._id}`)} className='p-6 pb-4'>
                                            <div className='flex items-start gap-3 mb-3'>
                                                <div className='flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-lg bg-secondary-100'>
                                                    <BookMarked className='w-5 h-5 text-secondary-600' />
                                                </div>
                                                <div className='flex-1 min-w-0'>
                                                    <h3 className='mb-2 font-semibold text-gray-900 line-clamp-2'>
                                                        {resource.title}
                                                    </h3>
                                                    {resource.description && (
                                                        <p className='text-sm text-gray-600 line-clamp-3'>
                                                            {resource.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className='flex items-center justify-between px-6 pb-6'>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleToggleFavorite(resource._id)
                                                }}
                                                className='inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors'
                                                aria-label={
                                                    isAlreadyFavorited ? 'Remove from favorites' : 'Add to favorites'
                                                }
                                            >
                                                <Bookmark
                                                    className={`w-4 h-4 ${isAlreadyFavorited ? 'text-red-500' : 'text-gray-400'}`}
                                                    fill={isAlreadyFavorited ? 'currentColor' : 'none'}
                                                />
                                                Remove
                                            </button>
                                            <button
                                                onClick={() => navigate(`/resource/${resource._id}`)}
                                                className='inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:text-brand-700'
                                            >
                                                View
                                                <ArrowUpRight className='w-4 h-4' />
                                            </button>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </>
                ) : (
                    <div className='max-w-md p-12 mx-auto text-center bg-white rounded-lg shadow-sm'>
                        <Heart className='w-16 h-16 mx-auto mb-6 text-gray-300' />
                        <h3 className='mb-3 text-xl font-medium text-gray-900'>No saved resources yet</h3>
                        <p className='mb-8 leading-relaxed text-gray-500'>
                            Discover valuable resources in our library and bookmark them for quick access later.
                        </p>
                        <div className='space-y-3'>
                            <Button
                                onClick={() => navigate('/resource-library')}
                                className='inline-flex items-center px-6 py-3 text-sm font-medium text-white transition-colors rounded-md bg-brand-600 hover:bg-brand-700'
                            >
                                Browse Resource Library
                                <ArrowUpRight className='w-4 h-4 ml-2' />
                            </Button>
                            <div className='text-xs text-gray-500'>Find resources tailored to your needs</div>
                        </div>
                    </div>
                )}
            </main>

            {user.favoriteResources && user.favoriteResources.length > 0 && (
                <footer className='py-8 mt-12 border-t border-gray-200'>
                    <div className='px-6 mx-auto sm:px-10 max-w-7xl'>
                        <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
                            <div className='text-sm text-gray-600'>Want to find more resources?</div>
                            <div className='flex flex-col gap-3 sm:flex-row'>
                                <Button
                                    onClick={() => navigate('/resource-library')}
                                    className='inline-flex items-center px-4 py-2 text-sm font-medium text-white transition-colors rounded-md bg-brand-600 hover:bg-brand-700'
                                >
                                    Browse Library
                                    <ArrowUpRight className='w-4 h-4 ml-2' />
                                </Button>
                            </div>
                        </div>
                    </div>
                </footer>
            )}

            <footer className='py-6 pb-24 mt-12 border-t border-gray-200'>
                <div className='px-6 mx-auto sm:px-10 max-w-7xl'>
                    <div className='flex flex-col items-center gap-4 text-center'>
                        <div className='flex flex-col gap-4 sm:flex-row sm:gap-8'>
                            <span
                                onClick={() => navigate('/terms-of-service')}
                                className='text-sm text-gray-500 underline transition-colors cursor-pointer hover:text-brand-600'
                            >
                                Terms of Service
                            </span>
                            <span
                                onClick={() => navigate('/privacy-policy')}
                                className='text-sm text-gray-500 underline transition-colors cursor-pointer hover:text-brand-600'
                            >
                                Privacy Policy
                            </span>
                        </div>
                        <p className='text-xs text-gray-500'>© 2025 BrightPath. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    )
}
